import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ContentChildren, QueryList, AfterContentInit, TemplateRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { ProjectInfo } from '../models/project-info';
import { GridCellDirective } from './grid-cell.directive';
import { TranslationService } from '../i18n/translation.service';
import {
  DEFAULT_PROJECT_GRID_VIEW,
  ProjectGridFilter,
  ProjectGridColumnDef,
  ProjectGridModel,
  ProjectGridProjectRow,
  ProjectGridRow,
  ProjectGridView,
} from '../models/project-grid.types';
import { sortIndicator } from '../services/project-grid-actions';
import { columnClass } from '../services/project-grid-view';

/**
 * The one column that absorbs leftover width.
 *
 * Project names are the longest values in the grid and the ones people scan, so
 * they get the slack rather than it pooling in an empty gutter on the right.
 */
const DEFAULT_FLEXIBLE_COLUMN_ID = 'ProjectName';

/** Never narrower than this, however tight the viewport. */
const FLEXIBLE_COLUMN_MIN_PX = 210;

/**
 * The project selection grid. PURELY PRESENTATIONAL: it renders a model somebody
 * else computed and emits what the user did to it.
 *
 * WHY IT IS A COMPONENT AT ALL. It used to be ~200 lines of markup and ~200 lines
 * of logic inside main-layout — a 2,131-line file that also owns the nav, the
 * upload, the export and the telemetry stream — with the column list written out
 * TWICE, once for Live rows and once, character for character, for Historical.
 * Adding a column meant editing both blocks and two copies of a
 * grid-template-columns rule, and forgetting one produced a grid whose header and
 * body disagreed.
 *
 * WHY OnPush AND A PRECOMPUTED MODEL. The old pane resolved every value through a
 * component getter during change detection: getFilteredProjects() filtered and
 * sorted a fresh array on every call and was reached six-plus times per cycle, and
 * getAllUserParameterNames() — a full scan of every project's every parameter —
 * ran once per header cell AND once per body cell. Six UDF columns over a hundred
 * rows is six hundred full scans per cycle, on the field iPads this product runs
 * on. Nothing here computes: the cells arrive precomputed and aligned to the
 * columns, and the template only reads them.
 */
@Component({
  selector: 'app-project-grid',
  templateUrl: './project-grid.component.html',
  styleUrls: ['./project-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectGridComponent implements AfterContentInit {
  /** The computed model. A NEW OBJECT REFERENCE is what re-renders the grid. */
  /**
   * ANY ROW PAYLOAD. Angular components cannot be generic across a template
   * boundary without contextual typing gymnastics, so the input is widened here
   * and the CONSUMER keeps the real type — devices builds a
   * ProjectGridModel<AdminDevice>, projects a ProjectGridModel<ProjectInfo>, and
   * each keeps full checking on its own side.
   *
   * The grid itself never reads a payload field except in the share-link branch,
   * which is project-specific — see showShareLink.
   */
  @Input() model: ProjectGridModel<any> = {
    columns: [],
    rows: [],
    counts: { total: 0, live: 0, historical: 0, filtered: 0 },
  };

  /**
   * The saved view. Read ONLY for the header indicators and the filter row's current
   * values — the rows themselves come precomputed in `model`, so this never triggers
   * a recomputation here.
   */
  @Input() view: ProjectGridView = DEFAULT_PROJECT_GRID_VIEW;

  /** Rendered next to the project name when the deployment has share links on. */
  /**
   * The key of the selected row, or null.
   *
   * The projects grid NAVIGATES on click and so has no selected state; the admin
   * screens SELECT, because their Edit and Delete act on a selection and the user
   * has to see which row that is. Keyed rather than by object identity, so a row
   * rebuilt by a refresh still reads as selected.
   */
  @Input() selectedKey: string | null = null;


  /** The project whose link was just copied, for the transient "✓ Copied" label. */
  /**
   * Whether THIS USE of the shared grid gets a copy-link button.
   *
   * NOT a feature flag — the environment flag it used to pair with is gone and
   * share links are on for everyone. This is a per-usage capability, and it
   * defaults to false for a reason: this grid is reused by the admin screens
   * (drums, devices, customers, users), and a share link is meaningless on a
   * row that is not a project. Deleting it along with the env flag put a copy
   * button on every admin grid, which is how the grid specs caught it.
   */
  @Input() showShareLink = false;

  @Input() shareLinkCopiedFor: string | null = null;

  /** Header clicked. `additive` is a shift-click: refine the sort rather than replace it. */
  @Output() sortToggle = new EventEmitter<{ columnId: string; additive: boolean }>();

  @Output() filterChange = new EventEmitter<{ columnId: string; filter: ProjectGridFilter | null }>();

  /**
   * Consumer-supplied cell renderers, keyed by column id. Columns without one
   * render their precomputed text, which is still the default and the fast path.
   */
  @ContentChildren(GridCellDirective) cellTemplates = new QueryList<GridCellDirective>();

  private templateByColumn = new Map<string, TemplateRef<unknown>>();

  constructor(private cdr: ChangeDetectorRef, private translation: TranslationService) {}

  ngAfterContentInit(): void {
    this.indexTemplates();
    // RE-RENDER ONCE THE TEMPLATES ARE KNOWN. ContentChildren resolve in
    // ngAfterContentInit, which runs AFTER the first pass has already rendered
    // the rows — so every projected cell fell back to its plain text on first
    // paint, and only corrected itself if something else happened to trigger
    // change detection. On the devices screen that meant the status dot and the
    // unlock button were missing until you clicked something.
    this.cdr.detectChanges();
    // Rebuilt on change rather than looked up per cell per cycle: a Map lookup
    // in the template would run for every cell on every pass, which is the cost
    // this component was built to avoid.
    this.cellTemplates.changes.subscribe(() => this.indexTemplates());
  }

  private indexTemplates(): void {
    this.templateByColumn = new Map(
      this.cellTemplates.map((t) => [t.columnId, t.template])
    );
  }

  templateFor(columnId: string): TemplateRef<unknown> | null {
    return this.templateByColumn.get(columnId) ?? null;
  }

  @Output() selectProject = new EventEmitter<any>();
  @Output() copyLink = new EventEmitter<{ project: ProjectInfo; event: MouseEvent }>();  // project-specific by design

  /**
   * CSS grid track sizes, derived from the columns actually being rendered.
   *
   * PROJECT NAME TAKES THE SLACK. Every column had a fixed px width, and their
   * sum (~840px for the default set) left dead space inside the table once the
   * shell stopped being boxed at 810px — a wide, empty gutter to the right of
   * the last column while the longest values in the grid, the project names,
   * were the ones being truncated.
   *
   * `minmax(210px, 1fr)` gives that spare width to the name column and nothing
   * else: it never drops below the readable floor, and on a narrow viewport the
   * grid behaves exactly as it did before, because there is no slack to take.
   *
   * Keyed off the column ID rather than position, so a saved view that reorders
   * or hides columns still grows the right one — and if the name column is
   * hidden entirely, every track stays fixed and the old behaviour returns.
   */
  /**
   * Which column absorbs leftover width. Per-grid, because "the longest value
   * people scan" is a different column on every screen — and a grid whose flexible
   * column is not present leaves every track fixed, which is what made the drums
   * columns bunch left with dead space beside them.
   */
  @Input() flexibleColumnId = DEFAULT_FLEXIBLE_COLUMN_ID;

  get gridTemplateColumns(): string {
    return this.model.columns
      .map((column) =>
        column.id === this.flexibleColumnId
          ? `minmax(${FLEXIBLE_COLUMN_MIN_PX}px, 1fr)`
          : `${column.width}px`
      )
      .join(' ');
  }

  // Narrows over ANY payload, so the negative branch still narrows to a section
  // row and its label stays typed. Pinned to ProjectGridRow it stopped narrowing
  // the moment the model became generic.
  isProjectRow = (row: ProjectGridRow<any>): row is ProjectGridProjectRow<any> =>
    row.kind === 'project';

  /**
   * The per-column CSS hook, by POSITION IN THE RENDERED MODEL — which is safe here
   * precisely because it resolves through model.columns to the column's ID. The
   * class that lands on the element is keyed to the column, so a user whose saved
   * view reorders or hides columns still gets the right hook on the right cell.
   *
   * Returns '' rather than throwing when a cell has no column: cells are built from
   * columns so they align, but a mismatch should drop a style, not the grid.
   */
  columnClassAt(index: number): string {
    const column = this.model.columns[index];
    if (!column) return '';
    // The id class is what a consumer targets for one specific column. The TYPE
    // class is what makes every grid agree: digits line up column-wise wherever
    // they appear, instead of only on the five project columns that used to be
    // named one by one in the stylesheet — which is why an admin grid's counts
    // and lengths did not align like the projects grid's did.
    const byType = column.type === 'number' || column.type === 'date' ? ' col-numeric' : '';
    return columnClass(column.id) + byType;
  }

  /** Which way this column is sorted and where it sits in a multi-column sort. */
  indicatorFor(columnId: string) {
    return sortIndicator(this.view, columnId);
  }

  /** The arrow a header shows. Blank when unsorted, so the sorted ones stand out. */
  sortGlyph(columnId: string): string {
    const indicator = this.indicatorFor(columnId);
    if (!indicator) return '';
    // The ordinal only appears once a second column is involved — a lone "1" next to
    // the only sorted column is noise.
    const ordinal = this.view.sort.length > 1 ? String(indicator.index + 1) : '';
    return (indicator.direction === 'asc' ? '▲' : '▼') + ordinal;
  }

  onHeaderClick(columnId: string, event: MouseEvent): void {
    this.sortToggle.emit({ columnId, additive: event.shiftKey });
  }

  filterValue(columnId: string): string {
    return this.view.filters?.[columnId]?.value ?? '';
  }

  onFilterInput(columnId: string, value: string): void {
    this.filterChange.emit({
      columnId,
      filter: value.trim() ? { op: 'contains', value } : null,
    });
  }

  // ── Enum filters ───────────────────────────────────────────────────────────
  //
  // Multi-select semantics, matching what the model already does: OR WITHIN a
  // column ("Active or Active (Free)"), AND ACROSS columns. That is the `in`
  // operator, which passesFilters has supported since the filters landed — the
  // work here is a control that can express it.

  /** Which column's menu is open. One at a time: two open menus overlap. */
  openEnumFilter: string | null = null;

  toggleEnumFilter(columnId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openEnumFilter = this.openEnumFilter === columnId ? null : columnId;
  }

  /**
   * Close on any click elsewhere. Bound on the document rather than a backdrop
   * element, so the click that closes the menu still reaches whatever it landed
   * on — a backdrop would swallow it and every dismissal would cost two clicks.
   */
  @HostListener('document:click')
  closeEnumFilter(): void {
    this.openEnumFilter = null;
  }

  private selectedEnumValues(columnId: string): string[] {
    const filter = this.view.filters?.[columnId];
    return filter?.op === 'in' ? filter.values ?? [] : [];
  }

  isEnumSelected(columnId: string, value: string): boolean {
    return this.selectedEnumValues(columnId).includes(value);
  }

  /** No selection IS "all" — see the note on clearing in the template. */
  isEnumAll(columnId: string): boolean {
    return this.selectedEnumValues(columnId).length === 0;
  }

  clearEnumFilter(columnId: string): void {
    this.filterChange.emit({ columnId, filter: null });
  }

  toggleEnumValue(columnId: string, value: string): void {
    const current = this.selectedEnumValues(columnId);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    // Unticking the last value returns to "all" rather than leaving a filter that
    // matches nothing — an empty grid with a filter set is the state people
    // report as a bug.
    this.filterChange.emit({
      columnId,
      filter: next.length ? { op: 'in', values: next } : null,
    });
  }

  /** What the closed control says. Names one or two; counts beyond that. */
  enumSummary(column: ProjectGridColumnDef): string {
    const selected = this.selectedEnumValues(column.id);
    if (selected.length === 0) return column.label;
    if (selected.length <= 2) {
      return selected
        .map((value) => {
          const option = column.enumOptions?.find((o) => o.value === value);
          return option?.labelKey ? this.translation.t(option.labelKey) : value;
        })
        .join(', ');
    }
    return `${selected.length} selected`;
  }

  onSelect(row: ProjectGridProjectRow): void {
    this.selectProject.emit(row.data);
  }

  onCopyLink(row: ProjectGridProjectRow, event: MouseEvent): void {
    // The click must not also select the project — the button sits inside the row.
    event.stopPropagation();
    this.copyLink.emit({ project: row.data, event });
  }

  /**
   * Rows are keyed by project name, which is what identifies a project everywhere
   * else in this app (the selection, the deep link, the API). Without a trackBy the
   * whole body is torn down and rebuilt on every model change, which on an iPad is
   * visible as a flash.
   */
  trackRow = (index: number, row: ProjectGridRow): string =>
    row.kind === 'project' ? `p:${row.key}` : `s:${index}:${row.label}`;
}
